/* GASSISA
=========================================================================================
* Descripción: Monitor de créditos a clientes y recepción de abonos.
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 24/10/2026 | Lic. José Luis Aragón Cervantes  | Creación para Tenant
*      2       | 24/10/2026 | Lic. José Luis Aragón Cervantes  | Menú de acciones estándar
*      3       | 24/10/2026 | Lic. José Luis Aragón Cervantes  | Abonos y estados de cuenta por folio de crédito
* ---------------------------------------------------------------------------------------
*/

import React, { useMemo, useState } from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, Paper, Stack, Tooltip, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PrintIcon from "@mui/icons-material/Print";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useNavigate } from "react-router-dom";
import ModalBuscarClientes from "../herramientas/ModalBuscarClientes";
import ModalCobrosGenerales from "../ventas/ModalCobrosGenerales";
import SizeSoftXLayoutForm from "../leyout/SizeSoftXLayoutForm";
import SizeSoftXTablePaginadoFilter from "../leyout/SizeSoftXTablePaginadoFilter";
import MnuCuentasXcobrarActions from "./MnuCuentasXcobrarActions";
import { useLayout } from "../../context/System/LayoutContext";
import { useRequest } from "../../context/System/RequestContext";
import { useSesion } from "../../context/System/SesionContext";
import { useVentas } from "../../context/Ventas/VentasContext";
import {
    apiCreditoClientesEstadoCuenta,
    apiCreditoClientesRegistrarAbono,
    apiCreditoClientesReimprimirAbono,
} from "../../settings/ApiConfig";
import { getDateString, NumberFormat$ } from "../../settings/utils";
import {
    buildTicketAbonoCreditoParts,
    imprimirTicketAbonoCredito,
} from "../../settings/ticketAbonoCredito";

const estadoInicial = {
    cargos: [],
    abonos: [],
    movimientos: [],
    totalcargo: 0,
    totalabono: 0,
    saldo: 0,
};

const numero = (value) => Number(value || 0);
const getFolioCredito = (row) => numero(row?.foliocredito);

const asignarIdentificadorMovimiento = (movimientos) =>
    movimientos.map((movimiento, index) => {
        const tipo = String(movimiento?.tipo || "").toUpperCase();
        const folioCredito = getFolioCredito(movimiento);
        const folioAbono = numero(movimiento?.folioabono);
        return {
            ...movimiento,
            idmovimiento: tipo === "CARGO"
                ? `C-${folioCredito}`
                : `A-${folioCredito}-${folioAbono || index}`,
        };
    });

export default function FrmCuentasXcobrar() {
    const navigate = useNavigate();
    const { GetRequest, PostRequest } = useRequest();
    const { handleOpenAlert } = useLayout();
    const { parametrosCajaForm, usuarioData } = useSesion();
    const { handleAbrirCajonDinero } = useVentas();
    const [openBuscarCliente, setOpenBuscarCliente] = useState(false);
    const [openCobro, setOpenCobro] = useState(false);
    const [cliente, setCliente] = useState({});
    const [estado, setEstado] = useState(estadoInicial);
    const [rowMovimiento, setRowMovimiento] = useState({});
    const [cargando, setCargando] = useState(false);
    const [observacion, setObservacion] = useState("");
    const [openVistaPreviaEstado, setOpenVistaPreviaEstado] = useState(false);
    const [vistaPreviaLineas, setVistaPreviaLineas] = useState([]);
    const [montosCobro, setMontosCobro] = useState({
        total: 0,
        subTotal: 0,
        totalIVA: 0,
        totalDescuento: 0,
        montoEnLetra: "",
        tipoPago: "EFECTIVO",
    });

    const movimientos = useMemo(() => {
        if (Array.isArray(estado.movimientos)) return asignarIdentificadorMovimiento(estado.movimientos);
        return [];
    }, [estado.movimientos]);

    const nombreCliente = () => cliente?.razonsocial || estado?.nombrecliente || cliente?.nombrecliente || "";

    const cuentaSeleccionada = useMemo(() => {
        const folioCredito = getFolioCredito(rowMovimiento);
        if (folioCredito <= 0) return null;
        return (estado.cargos || []).find(
            (cuenta) => getFolioCredito(cuenta) === folioCredito
        ) || null;
    }, [estado.cargos, rowMovimiento]);

    const movimientosCuenta = useMemo(() => {
        const folioCredito = getFolioCredito(cuentaSeleccionada);
        if (folioCredito <= 0) return [];
        let saldoAcumulado = 0;
        return movimientos
            .filter((movimiento) => getFolioCredito(movimiento) === folioCredito)
            .map((movimiento) => {
                saldoAcumulado += numero(movimiento.cargo) - numero(movimiento.abono);
                return { ...movimiento, saldo: saldoAcumulado };
            });
    }, [cuentaSeleccionada, movimientos]);

    const totalCargoCuenta = numero(cuentaSeleccionada?.total);
    const totalAbonoCuenta = useMemo(
        () => movimientosCuenta.reduce((suma, movimiento) => suma + numero(movimiento.abono), 0),
        [movimientosCuenta]
    );
    const saldoCuenta = Math.max(totalCargoCuenta - totalAbonoCuenta, 0);
    const tieneCreditoSeleccionado = Boolean(cuentaSeleccionada?.foliocredito);

    const construirLineasEstadoCuenta = (cuenta = cuentaSeleccionada) => {
        if (!cuenta) return [];
        let saldoMovimiento = 0;
        const movimientosDelCredito = movimientos
            .filter((movimiento) => getFolioCredito(movimiento) === getFolioCredito(cuenta))
            .map((movimiento) => {
                saldoMovimiento += numero(movimiento.cargo) - numero(movimiento.abono);
                return { ...movimiento, saldo: saldoMovimiento };
            });
        const totalAbonos = movimientosDelCredito.reduce(
            (suma, movimiento) => suma + numero(movimiento.abono),
            0
        );
        const lineasMovimientos = movimientosDelCredito.length === 0
            ? ["Sin movimientos"]
            : movimientosDelCredito.map((movimiento) => {
                const fecha = movimiento.fecharegistro || movimiento.fechaabono || new Date().toISOString();
                const folio = movimiento.folioabono || movimiento.remision || "";
                return `${getDateString(fecha)} ${movimiento.tipo || ""} ${folio} C:${NumberFormat$(movimiento.cargo || 0)} A:${NumberFormat$(movimiento.abono || 0)} S:${NumberFormat$(movimiento.saldo || 0)}`;
            });

        return [
            "SIZESOFTX",
            "ESTADO DE CUENTA - CRÉDITO",
            "--------------------------------",
            "Fecha: " + getDateString(new Date().toISOString()),
            "Cliente: " + nombreCliente(),
            "Clave: " + (cliente?.clavecliente || estado?.clavecliente || ""),
            "Folio crédito: " + cuenta.foliocredito,
            "Folio venta referencia: " + (cuenta.remision || ""),
            "--------------------------------",
            "RESUMEN",
            "Total cargos: " + NumberFormat$(cuenta.total || 0),
            "Total abonos: " + NumberFormat$(totalAbonos),
            "Saldo: " + NumberFormat$(Math.max(numero(cuenta.total) - totalAbonos, 0)),
            "--------------------------------",
            "MOVIMIENTOS",
            ...lineasMovimientos,
            "--------------------------------",
        ];
    };

    const imprimirTicket = async (lineas) => {
        const impresora = parametrosCajaForm?.nombreimpresoraapartados?.trim()
            || parametrosCajaForm?.nombreimpresoraventas?.trim()
            || "";
        if (!impresora) {
            handleOpenAlert("No hay impresora configurada para tickets", "warning");
            return false;
        }

        const datos = new TextEncoder().encode(lineas.join("\n") + "\n\n\n");
        const formData = new FormData();
        formData.append("ticket", new Blob([datos], { type: "application/octet-stream" }));
        formData.append("printerName", impresora);
        const response = await fetch("http://localhost:4000/api/imprimirticket", { method: "POST", body: formData });
        if (!response.ok) throw new Error(response.statusText);
        return true;
    };

    const abrirVistaPreviaEstado = (movimiento = rowMovimiento) => {
        const folioCredito = getFolioCredito(movimiento);
        const cuenta = (estado.cargos || []).find(
            (cargo) => getFolioCredito(cargo) === folioCredito
        );
        if (!cuenta) {
            handleOpenAlert("Seleccione un crédito para ver su estado de cuenta", "warning");
            return;
        }
        setRowMovimiento(movimiento);
        setVistaPreviaLineas(construirLineasEstadoCuenta(cuenta));
        setOpenVistaPreviaEstado(true);
    };

    const imprimirEstadoCuenta = async () => {
        if (!cuentaSeleccionada) {
            handleOpenAlert("Seleccione un crédito para imprimir su estado de cuenta", "warning");
            return;
        }
        try {
            const impreso = await imprimirTicket(construirLineasEstadoCuenta(cuentaSeleccionada));
            if (impreso) handleOpenAlert("Estado de cuenta impreso correctamente", "success");
        } catch (error) {
            handleOpenAlert("No se pudo imprimir el estado de cuenta: " + (error?.message || error), "error");
        }
    };

    const cargarEstado = async (clienteSeleccionado = cliente, folioCreditoASeleccionar = 0) => {
        const clavecliente = (clienteSeleccionado?.clavecliente || clienteSeleccionado?.sClaveCliente || "").trim();
        if (!clavecliente) return;

        setCargando(true);
        try {
            const response = await GetRequest({
                url: apiCreditoClientesEstadoCuenta.replace("{clavecliente}", encodeURIComponent(clavecliente)),
                loader: "Consultando estado de cuenta...",
            });
            if (response.hasError) {
                handleOpenAlert(response.message, "error");
                return;
            }
            const resultado = response.result || estadoInicial;
            setEstado(resultado);
            const folioCredito = numero(folioCreditoASeleccionar);
            const movimiento = folioCredito > 0
                ? asignarIdentificadorMovimiento(resultado.movimientos || []).find(
                    (item) => getFolioCredito(item) === folioCredito
                )
                : null;
            setRowMovimiento(movimiento || {});
        } catch (error) {
            handleOpenAlert("No se pudo consultar el estado de cuenta: " + (error?.message || error), "error");
        } finally {
            setCargando(false);
        }
    };

    const seleccionarCliente = async (clienteSeleccionado) => {
        setCliente(clienteSeleccionado || {});
        setOpenBuscarCliente(false);
        await cargarEstado(clienteSeleccionado);
    };

    const abrirCobro = () => {
        if (!cliente?.clavecliente || !cuentaSeleccionada) {
            handleOpenAlert("Seleccione un crédito antes de recibir un abono", "warning");
            return;
        }
        if (saldoCuenta <= 0) {
            handleOpenAlert("El crédito seleccionado no tiene saldo pendiente", "warning");
            return;
        }
        setObservacion("");
        setMontosCobro({
            total: saldoCuenta,
            subTotal: saldoCuenta,
            totalIVA: 0,
            totalDescuento: 0,
            montoEnLetra: "",
            tipoPago: "EFECTIVO",
        });
        setOpenCobro(true);
    };

    const imprimirAbono = async (abono = {}) => {
        const folioCredito = getFolioCredito(abono) || getFolioCredito(cuentaSeleccionada);
        const cuentaAbono = (estado.cargos || []).find(
            (cuenta) => getFolioCredito(cuenta) === folioCredito
        ) || cuentaSeleccionada || {};
        const folioAbono = numero(abono.folioabono ?? abono.FolioAbono);
        const abonosCuenta = (estado.abonos || [])
            .filter((registro) => getFolioCredito(registro) === folioCredito)
            .map((registro) =>
                numero(registro.folioabono ?? registro.FolioAbono) === folioAbono && folioAbono > 0
                    ? { ...registro, ...abono }
                    : registro
            );

        if (!abonosCuenta.some((registro) =>
            numero(registro.folioabono ?? registro.FolioAbono) === folioAbono && folioAbono > 0
        )) {
            abonosCuenta.push(abono);
        }

        abonosCuenta.sort((a, b) => {
            const fechaA = new Date(a.fechaabono || a.fecharegistro || 0).getTime();
            const fechaB = new Date(b.fechaabono || b.fecharegistro || 0).getTime();
            if (fechaA !== fechaB) return fechaA - fechaB;
            return numero(a.folioabono ?? a.FolioAbono) - numero(b.folioabono ?? b.FolioAbono);
        });

        const indiceAbono = Math.max(0, abonosCuenta.findIndex((registro) =>
            numero(registro.folioabono ?? registro.FolioAbono) === folioAbono && folioAbono > 0
        ));
        const totalAbonosPrevios = abonosCuenta
            .slice(0, indiceAbono)
            .reduce((suma, registro) => suma + numero(registro.montoabono ?? registro.MontoAbono), 0);
        const montoAplicado = numero(abono.montoabono ?? abono.MontoAbono);
        const totalAbonado = totalAbonosPrevios + montoAplicado;
        const saldoAnterior = Math.max(numero(cuentaAbono.total) - totalAbonosPrevios, 0);
        const saldoActual = Math.max(numero(cuentaAbono.total) - totalAbonado, 0);
        const impresora = parametrosCajaForm?.nombreimpresoraapartados?.trim()
            || parametrosCajaForm?.nombreimpresoraventas?.trim()
            || "";

        if (!impresora) {
            handleOpenAlert("No hay impresora configurada para tickets", "warning");
            return false;
        }

        try {
            const { partes } = buildTicketAbonoCreditoParts({
                parametrosCajaForm,
                abono: {
                    ...abono,
                    foliocredito: folioCredito,
                    montoabono: montoAplicado,
                    nombrecliente: abono.nombrecliente || nombreCliente(),
                },
                cuenta: cuentaAbono,
                cliente,
                totalAbonosPrevios,
                totalAbonado,
                saldoAnterior,
                saldoActual,
            });
            await imprimirTicketAbonoCredito({
                partes,
                parametrosCajaForm,
                nombreImpresora: impresora,
            });
            return true;
        } catch (error) {
            handleOpenAlert("No se pudo imprimir el ticket de abono: " + (error?.message || error), "error");
            return false;
        }
    };

    const reimprimirAbono = async (movimiento) => {
        const folioAbono = movimiento?.folioabono;
        if (!folioAbono) return;

        setCargando(true);
        try {
            const response = await GetRequest({
                url: apiCreditoClientesReimprimirAbono.replace("{folioabono}", folioAbono),
                loader: "Consultando abono...",
            });
            if (response.hasError) {
                handleOpenAlert(response.message, "error");
                return;
            }

            const impreso = await imprimirAbono(response.result || movimiento);
            if (impreso) handleOpenAlert("Ticket de abono reimpreso correctamente", "success");
        } catch (error) {
            handleOpenAlert("No se pudo reimprimir el abono: " + (error?.message || error), "error");
        } finally {
            setCargando(false);
        }
    };

    const registrarAbono = async (payload) => {
        if (!cliente?.clavecliente || !cuentaSeleccionada) {
            handleOpenAlert("Seleccione un crédito antes de registrar el abono", "warning");
            return;
        }
        const efectivo = Math.max(
            numero(payload.totalefectivo) - numero(payload.totalcambio),
            0
        );
        const montoTipoPago = payload.tipopago === "EFECTIVO"
            ? 0
            : numero(payload.totaltipopago);
        const monto = efectivo + montoTipoPago;
        if (monto <= 0) {
            handleOpenAlert("Capture un monto de abono válido", "warning");
            return;
        }

        const body = {
            clavecliente: cliente.clavecliente,
            nombrecliente: cliente.razonsocial || "",
            foliocredito: cuentaSeleccionada.foliocredito,
            foliocortecaja: parametrosCajaForm?.foliocortecaja || 0,
            montoabono: monto,
            efectivo,
            referencia: payload.serialtarjetachequevale || "",
            formapago: payload.tipopago || "EFECTIVO",
            observacion,
            usuario: usuarioData?.Usuario || "",
            folioabono: 0,
        };

        setCargando(true);
        try {
            const response = await PostRequest({
                url: apiCreditoClientesRegistrarAbono,
                body,
                loader: "Registrando abono a cuenta...",
            });
            if (response.hasError) {
                handleOpenAlert(response.message, "error");
                return;
            }
            setOpenCobro(false);
            if (efectivo > 0) await handleAbrirCajonDinero();
            await imprimirAbono(response.result || body);
            await cargarEstado(cliente, cuentaSeleccionada.foliocredito);
            handleOpenAlert("Abono registrado correctamente", "success");
        } catch (error) {
            handleOpenAlert("No se pudo registrar el abono: " + (error?.message || error), "error");
        } finally {
            setCargando(false);
        }
    };

    const columnsMovimientos = [
        { title: "Folio crédito", field: "foliocredito", width: 110 },
        {
            title: "Fecha",
            field: "fecharegistro",
            width: 120,
            render: (row) => getDateString(row.fecharegistro || row.fechaabono || new Date()),
        },
        { title: "Tipo", field: "tipo", width: 95 },
        { title: "Folio venta", field: "remision", width: 105 },
        { title: "Folio abono", field: "folioabono", width: 105 },
        { title: "Cargo", field: "cargo", width: 110, render: (row) => NumberFormat$(row.cargo || 0) },
        { title: "Abono", field: "abono", width: 110, render: (row) => NumberFormat$(row.abono || 0) },
        { title: "Saldo", field: "saldo", width: 110, render: (row) => NumberFormat$(row.saldo || 0) },
        { title: "Forma pago", field: "formapago", width: 130 },
        { title: "Observación", field: "observacion", width: 170 },
        {
            title: "",
            field: "__acciones",
            width: 80,
            hideSort: true,
            render: (row) => (
                <Stack direction="row" spacing={0}>
                    <Tooltip title="Vista previa del estado de cuenta">
                        <IconButton
                            size="small"
                            onClick={(event) => {
                                event.stopPropagation();
                                abrirVistaPreviaEstado(row);
                            }}
                        >
                            <VisibilityIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    {String(row.tipo || "").toUpperCase().includes("ABONO") && row.folioabono ? (
                        <Tooltip title="Reimprimir abono">
                            <IconButton
                                size="small"
                                disabled={cargando}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    reimprimirAbono(row);
                                }}
                            >
                                <PrintIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    ) : null}
                </Stack>
            ),
        },
    ];

    return (
        <>
            <SizeSoftXLayoutForm
                titlePage="Cuentas X Cobrar"
                buttonActions={
                    <MnuCuentasXcobrarActions
                        onSalir={() => navigate("/*")}
                        onBuscarCliente={() => setOpenBuscarCliente(true)}
                        onNuevoAbono={abrirCobro}
                        onVistaPreviaEstado={abrirVistaPreviaEstado}
                        disabledNuevoAbono={cargando || !tieneCreditoSeleccionado}
                        disabledVistaPrevia={cargando || !tieneCreditoSeleccionado}
                    />
                }
            >
                <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="subtitle1" fontWeight={700}>{nombreCliente() || "Seleccione un cliente"}</Typography>
                        <Typography variant="body2">Clave: {cliente?.clavecliente || "---"}</Typography>
                        <Stack direction="row" spacing={3} sx={{ mt: 1, flexWrap: "wrap" }}>
                            <Typography>Folio crédito: {cuentaSeleccionada?.foliocredito || "---"}</Typography>
                            <Typography>Cargo: {NumberFormat$(totalCargoCuenta)}</Typography>
                            <Typography>Abonos: {NumberFormat$(totalAbonoCuenta)}</Typography>
                            <Typography fontWeight={800}>Saldo del crédito: {NumberFormat$(saldoCuenta)}</Typography>
                        </Stack>
                    </Paper>
                    <Box sx={{ minHeight: 360, border: "1px solid #ddd", borderRadius: 1, overflow: "hidden" }}>
                        <SizeSoftXTablePaginadoFilter
                            columns={columnsMovimientos}
                            data={movimientos}
                            totalDeRegistros={movimientos.length}
                            id="idmovimiento"
                            row={rowMovimiento}
                            setRow={setRowMovimiento}
                            fontSize="small"
                            rowsPerPageSet={Math.max(movimientos.length, 10)}
                            disablePagination
                            maxHeight="55vh"
                        />
                    </Box>
                </Box>
            </SizeSoftXLayoutForm>

            <Dialog open={openVistaPreviaEstado} onClose={() => setOpenVistaPreviaEstado(false)} fullWidth maxWidth="sm">
                <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    Estado de cuenta - Crédito {cuentaSeleccionada?.foliocredito || ""}
                    <IconButton onClick={() => setOpenVistaPreviaEstado(false)} aria-label="Cerrar vista previa">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ maxHeight: "60vh", overflow: "auto", bgcolor: "#f3f4f6", p: 2, borderRadius: 1 }}>
                        <Box sx={{ width: "320px", mx: "auto", bgcolor: "#fff", color: "#111", border: "1px solid #ddd", boxShadow: 2, p: 1.5, fontFamily: "'Courier New', monospace", fontSize: 12, lineHeight: 1.35, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                            {vistaPreviaLineas.map((linea, index) => <Box key={index}>{linea || " "}</Box>)}
                        </Box>
                    </Box>
                </DialogContent>
                <Divider />
                <DialogActions>
                    <Button variant="contained" startIcon={<PrintIcon />} onClick={imprimirEstadoCuenta}>Imprimir</Button>
                    <Button variant="outlined" onClick={() => setOpenVistaPreviaEstado(false)}>Cerrar</Button>
                </DialogActions>
            </Dialog>

            <ModalBuscarClientes
                openModal={openBuscarCliente}
                handleCloseModal={() => setOpenBuscarCliente(false)}
                onClienteSelect={seleccionarCliente}
            />
            <ModalCobrosGenerales
                openModalCobrarVentas={openCobro}
                montosVenta={montosCobro}
                setMontosVenta={setMontosCobro}
                handleCloseModalCobroVentas={() => setOpenCobro(false)}
                handleCobrar={registrarAbono}
                bloquearCobroTotalSimple={false}
                calcularCambioEnLetra={false}
                observacion={observacion}
                onChangeObservacion={setObservacion}
            />
        </>
    );
}
